// ============================================
// GLOBAL DAYJS CONFIGURATION
// ============================================
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { ThemeContextProvider } from './theme/ThemeContext'
import './index.css'
import App from './App'
import reportWebVitals from './reportWebVitals'
import store from './app/store'
import { Provider } from 'react-redux'
import { BrowserRouter } from "react-router-dom"
import * as Sentry from "@sentry/react"
import { IS_DEV_ENV, SERVER_TIMEZONE } from "./config/consts"
import { SENTRY_ALWAYS_NOISE, createBeforeSend, beforeBreadcrumb } from "./lib/sentryNoise"
import { isReplaySuppressedPath } from "./lib/replayPolicy"
// Imported for its module-scope side effect: the credential is lifted out of the URL before
// Sentry.init snapshots it. Import order is the guarantee, so it is stated here, not inherited.
import "./utils/linkToken"
import { resolveSentryEnvironment } from "./lib/sentryEnv"
import { setSentryUser } from "./lib/sentryUser"
import { USER, safeGetItem } from "./lib/storage"

// Configure dayjs plugins and timezone
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.tz.setDefault(SERVER_TIMEZONE)
// ============================================

if (!IS_DEV_ENV) {
  Sentry.init({
    dsn: "https://2db5feb44a23cd0c056292908b7ff1ce@o4508222494670848.ingest.us.sentry.io/4508222496899072",
    // Session Replay records the address bar verbatim, and on the public link pages it also
    // records a client's name, amount and card form. Replay is therefore off by ROUTE, not by
    // whether the URL still looks token-bearing: once the token moves to the fragment a
    // shape-based test goes false and silently re-arms replay on exactly those pages.
    integrations: isReplaySuppressedPath(window.location.pathname)
      ? []
      : [Sentry.replayIntegration()],
    // NOT process.env.NODE_ENV: CRA sets that to "production" for every production build,
    // so staging events used to land in the production bucket. See lib/sentryEnv.js.
    environment: resolveSentryEnvironment({
      hostname: window.location.hostname,
      nodeEnv: process.env.NODE_ENV,
      override: process.env.REACT_APP_SENTRY_ENV,
    }),
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    ignoreErrors: SENTRY_ALWAYS_NOISE,
    denyUrls: [
      /maps\.googleapis\.com\/maps\/api\/js/,
    ],
    beforeSend: createBeforeSend(window.location.origin),
    beforeBreadcrumb,
  })

  // Sentry.init leaves the user scope empty. The three sign-in handlers set it once, but the
  // redirect that immediately follows discards that JS scope, so QA (2026-07-20) found every
  // later page load — i.e. effectively the whole session — reporting with no identity at all.
  // Re-establish it here, on every boot, from the stored user.
  try {
    const storedUser = safeGetItem(USER)
    if (storedUser) setSentryUser(JSON.parse(storedUser))
  } catch (error) {
    // a malformed stored user must never block app boot
  }
}


const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
  <Provider store={store}>
    <BrowserRouter>
      <ThemeContextProvider>
        <App />
      </ThemeContextProvider>
    </BrowserRouter>
  </Provider>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
